// In-game HUD: imperative DOM inside #hud-root. See docs/INTERNALS.md "Client module interfaces".
// Layout: unscaled center layer (crosshair, hit markers, damage arcs) plus corner zones whose content
// is scaled by `settings.hud.scale` (CSS `zoom`). `update(f)` runs every frame and only touches the DOM
// when a displayed value changed.

import "./hud.css";
import "./hud-panels.css";
import type { Settings } from "../settings/schema";
import { defaultSettings } from "../settings/schema";
import { Banner, InteractPrompt } from "./banner";
import { Chat } from "./chat";
import { CrosshairLayer } from "./crosshair";
import type { HitKind } from "./crosshair";
import { DamageLayer } from "./damage";
import { div } from "./dom";
import { Killfeed } from "./killfeed";
import { MatchBar, ObjectiveLine } from "./match";
import { MatchEnd } from "./matchend";
import { DeathPanel, LobbyHint, ScopeOverlay, SpectatorLabel } from "./panels";
import { Scoreboard } from "./scoreboard";
import { NetGraph, PerfReadout, Speedometer } from "./stats";
import type {
  ChatMessage,
  DeathInfo,
  HudFrame,
  KillfeedEntry,
  MatchEndView,
  MatchHudView,
  NetGraphStats,
  ObjectiveHudView,
  ScoreboardView,
} from "./types";
import { Vitals } from "./vitals";

export type { HitKind } from "./crosshair";
export type {
  ChatMessage,
  DeathInfo,
  HudFrame,
  HudMode,
  KillfeedEntry,
  MatchEndView,
  MatchHudView,
  MatchPhase,
  NetGraphStats,
  ObjectiveHudView,
  ScoreboardRow,
  ScoreboardView,
  TeamTag,
} from "./types";

function zone(cls: string, ...kids: HTMLElement[]): HTMLDivElement {
  const outer = div(`hud-zone ${cls}`);
  const inner = div("hud-zoom");
  inner.append(...kids);
  outer.append(inner);
  return outer;
}

export class Hud {
  private readonly container = div("hud");
  private readonly resizeObserver: ResizeObserver;

  private readonly scopeOverlay = new ScopeOverlay();
  private readonly crosshairLayer = new CrosshairLayer();
  private readonly damageLayer = new DamageLayer();
  private readonly vitals = new Vitals(this.container);
  private readonly perf = new PerfReadout();
  private readonly netView = new NetGraph();
  private readonly matchBar = new MatchBar();
  private readonly objective = new ObjectiveLine();
  private readonly feed = new Killfeed();
  private readonly chat = new Chat();
  private readonly speedo = new Speedometer();
  private readonly spectator = new SpectatorLabel();
  private readonly lobbyHintView = new LobbyHint();
  private readonly bannerView = new Banner();
  private readonly prompt = new InteractPrompt();
  private readonly deathPanel = new DeathPanel();
  private readonly scoreboard = new Scoreboard();
  private readonly endOverlay = new MatchEnd();

  private settings: Settings = defaultSettings();
  private netStats: NetGraphStats | null = null;
  private visible = true;
  private scoped = false;

  constructor(root: HTMLElement) {
    const c = this.container;
    c.append(
      this.scopeOverlay.root,
      div("hud-lowvig"),
      this.damageLayer.root,
      this.crosshairLayer.root,
      zone("hud-zone--tl", this.perf.root, this.netView.root),
      zone("hud-zone--tc", this.matchBar.root, this.objective.root, this.bannerView.root),
      zone("hud-zone--tr", this.feed.root),
      zone("hud-zone--bl", this.chat.root, this.vitals.health),
      zone("hud-zone--bc", this.lobbyHintView.root, this.spectator.root, this.speedo.root),
      zone("hud-zone--br", this.vitals.weapon),
      zone("hud-zone--prompt", this.vitals.hint, this.prompt.root, this.deathPanel.root),
      this.scoreboard.root,
      this.endOverlay.root,
    );
    root.append(c);

    this.resizeObserver = new ResizeObserver(() => this.measure(root));
    this.resizeObserver.observe(root);
    this.measure(root);
    this.applySettings(this.settings);
  }

  applySettings(s: Settings): void {
    this.settings = s;
    this.container.style.setProperty("--hud-scale", String(s.hud.scale));
    this.crosshairLayer.applySettings(s);
    this.vitals.applySettings(s);
    this.perf.setVisible(s.hud.showFps, s.hud.showPing);
    this.speedo.setVisible(s.hud.showSpeed);
    this.netView.setStats(this.netStats, s.hud.showNetGraph);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.container.hidden = !v;
    if (!v) this.chat.close();
  }

  /** Every frame. Allocation-free; DOM writes only for values that changed. */
  update(f: HudFrame): void {
    if (!this.visible) return;
    const now = performance.now();
    this.crosshairLayer.update(f.showCrosshair && !this.scoped, f.spreadDeg, f.hFovDeg, f.reticleDot);
    this.vitals.update(f);
    this.perf.update(f.fps, f.pingMs, now);
    this.speedo.update(f.speed, now);
    this.netView.update(now);
    this.feed.update(now);
    this.chat.update(now);
    this.matchBar.update(now);
    this.deathPanel.update(now);
    this.endOverlay.update(now);
  }

  hitmarker(kind: HitKind): void {
    this.crosshairLayer.hitmarker(kind);
  }

  /** `dirRad`: source direction relative to the view yaw, 0 = ahead, + = left. */
  damage(dirRad: number, amount: number): void {
    this.damageLayer.hit(dirRad, amount);
  }

  killfeed(e: KillfeedEntry): void {
    this.feed.push(e, performance.now());
  }

  setScoreboard(d: ScoreboardView): void {
    this.scoreboard.set(d);
    this.matchBar.setRows(d.rows);
  }

  showScoreboard(v: boolean): void {
    this.scoreboard.show(v);
  }

  setMatch(m: MatchHudView): void {
    this.matchBar.set(m, performance.now());
    this.chat.setTeamCapable(m.mode !== "ffa" && m.myTeam !== "none");
  }

  setObjective(o: ObjectiveHudView | null): void {
    this.objective.set(o);
  }

  banner(title: string, sub?: string, tone: "neutral" | "good" | "bad" = "neutral"): void {
    this.bannerView.show(title, sub, tone);
  }

  interact(prompt: string | null, progress: number | null): void {
    this.prompt.set(prompt, progress);
  }

  perfectHop(): void {
    this.crosshairLayer.perfectHop();
  }

  chatPush(m: ChatMessage): void {
    this.chat.push(m, performance.now());
  }

  /** Enter sends, Esc closes. While open the game must not handle keys (`chatIsOpen()`). */
  chatOpen(teamOnly: boolean, onSend: (text: string, teamOnly: boolean) => void): void {
    this.chat.open(teamOnly, onSend, performance.now());
  }

  chatIsOpen(): boolean {
    return this.chat.isOpen();
  }

  /** Show (or update) the death panel; null hides it. Safe to call every frame. */
  death(info: DeathInfo | null): void {
    const was = this.deathPanel.isActive;
    this.deathPanel.set(info, performance.now());
    const now = info !== null;
    if (now === was) return;
    this.vitals.setDead(now);
    this.vitals.resetAmmoTracking(); // a new life may come with a new loadout
    if (now) {
      this.crosshairLayer.clear();
      this.damageLayer.clear();
    }
  }

  /** Custom lobby in warmup with the panel closed: "L  LOBBY ..." above the speed meter. */
  lobbyHint(show: boolean, host: boolean, keyLabel: string): void {
    this.lobbyHintView.set(show, host, keyLabel);
  }

  /** `hint`: what the spectator can do ("FIRE: NEXT PLAYER"), shown next to the name. */
  spectating(name: string | null, hint: string | null = null): void {
    this.spectator.set(name, hint);
  }

  scope(v: boolean): void {
    this.scoped = v;
    this.scopeOverlay.set(v);
  }

  /** Pass the live stats object (read ~4 times a second) or null to hide the graph. */
  netGraph(stats: NetGraphStats | null): void {
    this.netStats = stats;
    this.netView.setStats(stats, this.settings.hud.showNetGraph);
  }

  matchEnd(view: MatchEndView | null): void {
    this.endOverlay.set(view, performance.now());
    this.container.classList.toggle("hud--ended", view !== null);
  }

  private measure(root: HTMLElement): void {
    const h = root.clientHeight > 0 ? root.clientHeight : window.innerHeight;
    const w = root.clientWidth > 0 ? root.clientWidth : window.innerWidth;
    const dpr = window.devicePixelRatio || 1;
    this.crosshairLayer.setViewport(w, h, dpr);
    this.netView.setDpr(dpr);
  }
}
